/**
 * Whether the open tab has been answered with the demo of a closed feature
 * (#2816) by its CURRENT request — the tab's own keys under the section's
 * current window and filter (`useVoiceTabQueryKeys`). The banner over the
 * section follows that answer rather than the plan alone, and not any voice
 * answer ever cached: a tab that was refused (403) says so in its own words, a
 * period whose request is still on its way has nothing to say yet, and a
 * banner «this is demo data» over either would be a lie.
 *
 * Only an answer that says so itself counts (`demoOnly: true`) — the Sales
 * tab's objections stream too: it is open to every package and outside the demo
 * window returns the organization's real rows, so its success alone proves
 * nothing; the server marks it when it served the stream from demo rows alone.
 * It still never labels a tab one of whose blocks the plan refused: that tab is
 * the plan screen.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import { tierRefusalCode } from '@/lib/tier-refusal';
import { isDemoOnlyAnswer } from '@/features/demo/locked-demo';
import { useVoiceTabQueryKeys } from './useVoiceOfCustomer';
import type { VoiceAudience, VoiceSearch } from './voice-search';

export function useVoiceDemoOnlyAnswered(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  audience: VoiceAudience
): boolean {
  const queryClient = useQueryClient();
  const { blocks, stream } = useVoiceTabQueryKeys(search, onSearchChange, audience);
  // Only the events of these very queries wake the banner: the cache holds
  // every answer of the app, and `subscribe` hears them all.
  const hashes = [...blocks, ...stream].map((queryKey) => hashKey(queryKey));
  const watched = hashes.join('|');
  const subscribe = useCallback(
    (notify: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        if (hashes.includes(event.query.queryHash)) notify();
      }),
    // `watched` stands for `hashes`: the same keys, one stable string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, watched]
  );
  return useSyncExternalStore(subscribe, () => {
    // `state` of that very key, found by its hash: a placeholder (the previous
    // window's numbers while the new request loads) is the observer's, not the
    // query's.
    // A refusal first: a refetch the plan refused keeps the block's last answer
    // in `data` (the demo's, before the window closed), and the tab is the plan
    // screen now.
    const states = blocks.map((queryKey) => queryClient.getQueryState(queryKey));
    if (states.some((state) => tierRefusalCode(state?.error) !== null)) return false;
    if (states.some((state) => isDemoOnlyAnswer(state?.data))) return true;
    return stream.some((queryKey) =>
      isDemoOnlyAnswer(queryClient.getQueryState(queryKey)?.data)
    );
  });
}
